import { Button, Field, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import { generatePassword } from '../../../lib/generate-password.js';
import { TENANT_ADMIN_STATUS_OPTIONS, type TenantAdminFormInput } from '../api/schemas.js';

/**
 * A tenant administrator's inputs, independent of what surrounds them.
 *
 * Separated from the pages because create and edit are the same fields in two
 * containers, and a field set that lives inside one of them gets copied the
 * moment the other appears — after which they drift.
 *
 * Two fields differ, and both differences are the service's: an email and
 * password cannot be changed after issue, and a status cannot be chosen before
 * the administrator exists.
 */

export interface TenantAdminFieldsProps {
  readonly register: UseFormRegister<TenantAdminFormInput>;
  readonly errors: FieldErrors<TenantAdminFormInput>;
  readonly control: Control<TenantAdminFormInput>;
  readonly setValue: UseFormSetValue<TenantAdminFormInput>;
  readonly mode: 'create' | 'edit';
  readonly tenantOptions: readonly { readonly id: string; readonly name: string }[];
}

export function TenantAdminFields({
  register,
  errors,
  control,
  setValue,
  mode,
  tenantOptions,
}: TenantAdminFieldsProps): React.JSX.Element {
  const [revealed, setRevealed] = useState(false);

  const tenantSelectOptions = tenantOptions.map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
  }));

  return (
    <div className="space-y-6">
      <Field error={errors.tenantId?.message} required>
        <FieldLabel>Tenant</FieldLabel>
        <Controller
          control={control}
          name="tenantId"
          render={({ field }) => (
            <FieldSelect
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              options={tenantSelectOptions}
              placeholder="Select a tenant"
              disabled={mode === 'edit'}
              searchable
            />
          )}
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.email?.message} required={mode === 'create'}>
          <FieldLabel>Email</FieldLabel>
          <FieldInput
            type="email"
            autoComplete="off"
            readOnly={mode === 'edit'}
            className={mode === 'edit' ? 'bg-muted' : undefined}
            {...register('email')}
          />
        </Field>

        {mode === 'create' ? (
          <Field error={errors.password?.message} required>
            <FieldLabel>Initial password</FieldLabel>
            <div className="flex items-center gap-2">
              <FieldInput
                className="flex-1"
                type={revealed ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('password')}
              />
              <Button
                type="button"
                variant="outline"
                aria-label={revealed ? 'Hide password' : 'Show password'}
                onClick={() => {
                  setRevealed((previous) => !previous);
                }}
              >
                {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setValue('password', generatePassword(), {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setRevealed(true);
                }}
              >
                <RefreshCw className="h-4 w-4" /> Generate
              </Button>
            </div>
          </Field>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field error={errors.firstName?.message} required>
          <FieldLabel>First name</FieldLabel>
          <FieldInput autoComplete="off" {...register('firstName')} />
        </Field>

        <Field error={errors.lastName?.message}>
          <FieldLabel>Last name</FieldLabel>
          <FieldInput autoComplete="off" {...register('lastName')} />
        </Field>
      </div>

      {mode === 'create' ? null : (
        <Field error={errors.status?.message} required>
          <FieldLabel>Status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={[...TENANT_ADMIN_STATUS_OPTIONS]}
                placeholder="Select a status"
                searchable
              />
            )}
          />
        </Field>
      )}
    </div>
  );
}
